/**
 * Inline anomaly alert row for the Overview tab's alert feed. Shows the
 * rule name, fired time, metric value vs baseline, and an Acknowledge button.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { formatFloat } from '../format';
import { cn } from '../../../../lib/cn';

export interface AnomalyEvent {
  id: number;
  fired_at: string;
  rule_key: string;
  name: string;
  metric: string;
  metric_value: number;
  baseline: number;
  deviation: number;
  description?: string | null;
}

export interface AnomalyAlertCardProps {
  event: AnomalyEvent;
  onAcknowledge?: (id: number) => void;
  isAcknowledging?: boolean;
  className?: string;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const m = Math.round(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export const AnomalyAlertCard: React.FC<AnomalyAlertCardProps> = ({
  event,
  onAcknowledge,
  isAcknowledging,
  className,
}) => {
  const deviationAbs = Math.abs(event.deviation);
  const severity = deviationAbs >= 3 ? 'high' : deviationAbs >= 2 ? 'medium' : 'low';
  const sevRing = {
    high: 'ring-rose-200 bg-rose-50/60',
    medium: 'ring-amber-200 bg-amber-50/60',
    low: 'ring-gray-200 bg-gray-50/60',
  }[severity];
  const sevIcon = {
    high: 'text-rose-600',
    medium: 'text-amber-600',
    low: 'text-gray-500',
  }[severity];

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md p-3 ring-1 transition-colors',
        sevRing,
        className,
      )}
    >
      <AlertTriangle className={cn('mt-0.5 h-4 w-4 shrink-0', sevIcon)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-semibold text-gray-900">{event.name}</p>
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
            <Clock className="h-3 w-3" />
            {timeAgo(event.fired_at)}
          </span>
        </div>
        <p className="mt-0.5 font-mono text-[11px] text-gray-500">{event.metric}</p>
        <div className="mt-1 flex items-center gap-3 text-[11px] tabular-nums">
          <span className="text-gray-700">
            value <b>{formatFloat(event.metric_value)}</b>
          </span>
          <span className="text-gray-400">
            baseline <b>{formatFloat(event.baseline)}</b>
          </span>
          <span className={cn('font-semibold', sevIcon)}>
            Δ {formatFloat(event.deviation)}
          </span>
        </div>
        {event.description && (
          <p className="mt-1 text-[11px] text-gray-500">{event.description}</p>
        )}
      </div>
      {onAcknowledge && (
        <button
          type="button"
          onClick={() => onAcknowledge(event.id)}
          disabled={isAcknowledging}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-50"
        >
          <CheckCircle2 className="h-3 w-3" />
          Ack
        </button>
      )}
    </div>
  );
};
